import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import Menu from './pages/Menu';
import Order from './pages/Order';
import Admin from './pages/Admin';
import About from './pages/About';
import Contact from './pages/Contact';
import ProductDetail from './pages/ProductDetail';
import { ToastProvider } from './context/ToastContext';
import { initializeProducts } from './data/products';
import './App.css';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Initialize products from API when app loads
  useEffect(() => {
    const loadProducts = async () => {
      try {
        // Try to fetch products from API
        const products = await initializeProducts();
        console.log('Products initialized:', products);
        
        // Check if we need to populate the database (no products in any category)
        if (products && (
          products.smoothies.length === 0 && 
          products.juices.length === 0 && 
          products.proteinBalls.length === 0
        )) {
          console.log('No products found in database, attempting to add sample products...');
          
          try {
            const { createProduct } = await import('./services/api');
            
            // Add a sample product to the database
            const sampleProduct = {
              name: 'Green Goddess',
              description: 'Spinach, kale, banana, pineapple, and coconut water.',
              price: 8.99,
              category: 'smoothies',
              image: 'green-goddess.jpg',
              ingredients: ['Spinach', 'Kale', 'Banana', 'Pineapple', 'Coconut Water'],
              nutritionFacts: {
                calories: 220,
                protein: 5,
                fat: 1,
                carbs: 48
              }
            };
            
            const result = await createProduct(sampleProduct);
            console.log('Added sample product to database:', result);
            
            // Reload products after adding sample
            await initializeProducts();
          } catch (createError) {
            console.error('Failed to add sample product:', createError);
          }
        }
        
        setLoadError(null);
      } catch (error) {
        console.error('Failed to load products:', error);
        setLoadError('Unable to connect to the server. Using cached data if available.');
        // We continue anyway with locally cached data
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
  }, []);

  // Show loading screen if initial data is being fetched
  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Connecting to {process.env.REACT_APP_API_URL || 'backend'}...</p>
        <p className="loading-subtext">Please wait while we load the menu items</p>
      </div>
    );
  }

  return (
    <Router>
      <ToastProvider>
        <div className="App">
          {loadError && (
            <div className="api-error-banner">
              {loadError} API: {process.env.REACT_APP_API_URL || 'https://revive-bar-back.onrender.com'}
            </div>
          )}
          <Header />
          <main className="main-content">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/menu" element={<Menu />} />
              <Route path="/order" element={<Navigate to="/cart" replace />} />
              <Route path="/cart" element={<Order />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/product/:id" element={<ProductDetail />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </ToastProvider>
    </Router>
  );
}

export default App;
